import { Tour, TourProps } from 'antd';
import { getTourContent } from 'common/tourGuide/tourGuideApi';
import { useTourGuideStatus } from 'common/tourGuide/useBackendTour';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DraggableTourButton from './DraggableTourButton';

export const usePageTour = (storageKey: string, autoOpenDelay = 600) => {
  const [isTourOpen, setIsTourOpen] = useState(false);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem(storageKey);
    if (!hasSeenTour) {
      const timer = setTimeout(() => setIsTourOpen(true), autoOpenDelay);
      return () => clearTimeout(timer);
    }
  }, [storageKey, autoOpenDelay]);

  const closeTour = () => {
    setIsTourOpen(false);
    localStorage.setItem(storageKey, '1');
  };

  return { isTourOpen, setIsTourOpen, closeTour };
};

type PageTourProps = {
  storageKey: string;
  steps: TourProps['steps'];
  replayLabel: string;
  autoOpenDelay?: number;
  // Key 1 for this page in the superadmin-managed Tour Guide content (see
  // docs/TOUR_GUIDE_WEB_FRONTEND_GUIDE.md). When set, the bundled `steps`
  // prop is used ONLY to supply each step's DOM target (the backend has no
  // way to know where things are on screen) — every title/description comes
  // exclusively from the API. There is no bundled-text fallback: until the
  // superadmin configures JSON content for this key1 + the current
  // language, this page shows no tour at all.
  tourGuideKey1?: string;
};

type BackendStep = { target?: string; title: string; description: string };

export const PageTour = ({ storageKey, steps, replayLabel, autoOpenDelay, tourGuideKey1 }: PageTourProps) => {
  const { isTourOpen, setIsTourOpen, closeTour } = usePageTour(storageKey, autoOpenDelay);
  const { i18n } = useTranslation();
  // Shared RTK Query cache entry — one network call total, no matter how
  // many PageTour instances are mounted across the app.
  const { status, isLoading: statusLoading } = useTourGuideStatus();
  const [backendSteps, setBackendSteps] = useState<BackendStep[] | null>(null);
  const [contentLoading, setContentLoading] = useState(!!tourGuideKey1);

  useEffect(() => {
    if (!tourGuideKey1) return;
    let cancelled = false;
    setContentLoading(true);
    getTourContent(tourGuideKey1, i18n.language.split('-')[0])
      .then((entry) => {
        if (cancelled) return;
        if (!entry || entry.contentType !== 'json') {
          setBackendSteps(null);
          return;
        }
        const rawSteps: BackendStep[] = Array.isArray((entry.json as any)?.steps) ? (entry.json as any).steps : [];
        setBackendSteps(rawSteps.length ? rawSteps : null);
      })
      .catch(() => setBackendSteps(null))
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tourGuideKey1, i18n.language]);

  // The org Administrator turned the onboarding tour off for everyone —
  // never show, on any page.
  if (!statusLoading && status?.isEnabled === false) return null;

  // Pages wired to the Tour Guide API are API-only: no bundled i18n tour
  // while content hasn't loaded yet, failed to load, or hasn't been
  // configured for this key1 + language.
  if (tourGuideKey1) {
    if (contentLoading || !backendSteps) return null;

    const effectiveSteps: TourProps['steps'] = backendSteps.map((s, i) => ({
      title: s.title,
      description: s.description,
      target: steps?.[i]?.target,
    }));

    return (
      <>
        <Tour
          open={isTourOpen}
          onClose={closeTour}
          onFinish={closeTour}
          steps={effectiveSteps}
          indicatorsRender={(current, total) => (
            <span className="text-xs text-gray-400">
              {current + 1} / {total}
            </span>
          )}
        />
        <DraggableTourButton tooltip={replayLabel} onClick={() => setIsTourOpen(true)} />
      </>
    );
  }

  return (
    <>
      <Tour
        open={isTourOpen}
        onClose={closeTour}
        onFinish={closeTour}
        steps={steps}
        indicatorsRender={(current, total) => (
          <span className="text-xs text-gray-400">
            {current + 1} / {total}
          </span>
        )}
      />
      <DraggableTourButton tooltip={replayLabel} onClick={() => setIsTourOpen(true)} />
    </>
  );
};
